/*
This util file assists in correctly showing the process of a MQTT write.

Each MQTT value that can be written should have a 'mqttWriteValue' object, created using newMqttWriteValue. The 'busy'
property indicates when a write is still in progress, or perhaps timing out. 'error' contains an error in case something
went wrong. The result should ensure that the input is 'busy' until our write has finished.
 */

export const VALUE_WRITE_TIMEOUTS = {
    DEFAULT: 5000,
    RETRY_TIMEOUT: 2000,
    TIMEOUT_RECOVER: 4000,
};

export const VALUE_WRITE_ERRORS = {
    TIMED_OUT: 'could_not_reach_installation',
    ADJUSTED: 'write_value_adjusted',
    GENERAL: 'something_went_wrong',
};

export const newMqttWriteValue = ({ varName, varIdx }) => ({
    busy: false,
    timer: null,
    oldState: null,
    newState: null,
    error: null,
    timedOut: 0,
    varName,
    varIdx,
});

export function initiateValueLoading(writeValueObject) {
    if (writeValueObject.busy) window.clearTimeout(writeValueObject.timer);
    writeValueObject.busy = true;
    writeValueObject.timer = window.setTimeout(() => {
        this.resolveValue(writeValueObject);
    }, VALUE_WRITE_TIMEOUTS.DEFAULT);
}

export function resolveValue(writeValueObject, value = null) {
    writeValueObject.error = null;
    if (!writeValueObject.busy) return;

    if (value === null && !writeValueObject.timedOut) {
        // No new value was received on our initial attempt, this can mean the following:
        // - Time out, some kind of connection issue
        // - We wrote the same value, subscription was not updated
        // - We wrote a different value, but it was adjusted to the current value, subscription was not updated
        // To continue, mark it as timed out and send a read message to the path, to differentiate between the first
        // option and the other two. Set the timer to attempt the next resolve.

        writeValueObject.timedOut = 1;
        writeValueObject.timer = window.setTimeout(() => {
            this.resolveValue(writeValueObject);
        }, VALUE_WRITE_TIMEOUTS.RETRY_TIMEOUT);

        this.MQTTRealtimeService.publishToPath(
            {
                ...this.mqttPathObj,
                prefix: 'R/',
                callbackIdentifier:
                    'reading_' +
                    writeValueObject.varName +
                    (writeValueObject.varIndx !== undefined ? '_' + writeValueObject.varIndx : ''),
                ..._.find(
                    this.SUBSCRIPTIONS,
                    subscription =>
                        subscription.varName === writeValueObject.varName &&
                        (writeValueObject.varIndx === undefined ||
                            subscription.varIndx === writeValueObject.varIndx),
                ),
            },
            '',
        );
    } else if (value === null && writeValueObject.timedOut === 1) {
        // This write has timed out again, so we assume it has actually timed out now.
        writeValueObject.timedOut++;
        writeValueObject.error = VALUE_WRITE_ERRORS.TIMED_OUT;
    } else if (value !== null && writeValueObject.timedOut && writeValueObject.timedOut > 1) {
        // Time out / connection issue appears to have been resolved. Unfortunately we cannot reliably tell the
        // effects of our write attempt. Thus, we will wait a little longer and then free up the inputs for the user
        // again.
        writeValueObject.timer = window.setTimeout(() => {
            writeValueObject.busy = false;
            writeValueObject.timedOut = 0;
        }, VALUE_WRITE_TIMEOUTS.TIMEOUT_RECOVER);
    } else {
        // We have a result, check if we should display some kind of error.
        writeValueObject.error = detectValueError(writeValueObject, value);
        window.clearTimeout(writeValueObject.timer);
        writeValueObject.busy = false;
        writeValueObject.timedOut = 0;
    }

    //Used to show notifications on dashboard controls being changed (mobile). Refer to ControlsChangedNotificationMobile.vue
    if (
        writeValueObject.error === null &&
        parseFloat(value) === parseFloat(writeValueObject.newState)
    ) {
        window.dispatchEvent(new CustomEvent('show-notification'));
    } else {
        // If an error occurs, reset `noError` to false
        window.dispatchEvent(new CustomEvent('hide-notification'));
    }
}

function detectValueError(writeValueObject, value) {
    // Check for issues, if the new state we tried to set is not equal to the received state.
    if (value !== writeValueObject.newState) {
        if (value !== writeValueObject.oldState) {
            // We received a change, but it was not the one we requested. We will assume it was adjusted by the
            // receiving installation.
            return VALUE_WRITE_ERRORS.ADJUSTED;
        }
    }
    return null;
}
